export type FieldType =
  | "text"
  | "long_text"
  | "user"
  | "single_choice"
  | "multiple_choice"
  | "autocomplete"
  | "number"
  | "currency"
  | "date"
  | "time"
  | "address";

export type EntityType =
  | "deal"
  | "activity"
  | "lead"
  | "company"
  | "contact"
  | "product";

export interface EntityDefinition {
  id: EntityType;
  label: string;
  iconName: "dollar" | "calendar" | "target" | "building" | "user" | "package";
}

export const ENTITIES: EntityDefinition[] = [
  { id: "activity", label: "Atividade", iconName: "calendar" },
  { id: "deal", label: "Negócio", iconName: "dollar" },
  { id: "lead", label: "Cliente Potencial", iconName: "target" },
  { id: "company", label: "Organização", iconName: "building" },
  { id: "contact", label: "Pessoa", iconName: "user" },
  { id: "product", label: "Produto", iconName: "package" },
];

export interface FieldDefinition {
  id: string;
  label: string;
  type: FieldType;
  entity: EntityType;
  choices?: { value: string; label: string }[];
  isCustom?: boolean;
}

export type FilterOperator =
  // Texto, Texto longo, Endereço, Autocompletar
  | "é"
  | "não é"
  | "mudou para"
  | "contem"
  | "não contem"
  | "começa com"
  | "não começa com"
  | "termina com"
  | "não termina com"
  | "esta vazio"
  | "não esta vazio"
  // Opção única / Múltipla escolha
  | "contem algum de"
  | "não contem nem um de"
  // Número / Monetário
  | "="
  | "≠"
  | "<"
  | ">"
  | "≥"
  | "≤"
  // Data / Hora
  | "é exatamente ou antes de"
  | "é exatamente ou depois de"
  | "antes de"
  | "depois de";

export const OPERATORS_BY_FIELD_TYPE: Record<FieldType, FilterOperator[]> = {
  text: [
    "é",
    "não é",
    "mudou para",
    "contem",
    "não contem",
    "começa com",
    "não começa com",
    "termina com",
    "não termina com",
    "esta vazio",
    "não esta vazio",
  ],
  long_text: [
    "é",
    "não é",
    "contem",
    "não contem",
    "começa com",
    "não começa com",
    "termina com",
    "não termina com",
    "esta vazio",
    "não esta vazio",
  ],
  user: ["é", "não é", "mudou para", "esta vazio", "não esta vazio"],
  single_choice: [
    "é",
    "não é",
    "mudou para",
    "esta vazio",
    "não esta vazio",
    "contem algum de",
    "não contem nem um de",
    "contem",
    "não contem",
  ],
  multiple_choice: [
    "é",
    "não é",
    "mudou para",
    "esta vazio",
    "não esta vazio",
    "contem algum de",
    "não contem nem um de",
    "contem",
    "não contem",
  ],
  autocomplete: [
    "é",
    "não é",
    "mudou para",
    "contem",
    "não contem",
    "começa com",
    "não começa com",
    "termina com",
    "não termina com",
    "esta vazio",
    "não esta vazio",
  ],
  number: ["=", "≠", "esta vazio", "não esta vazio", "<", ">", "≥", "≤"],
  currency: ["=", "≠", "esta vazio", "não esta vazio", "<", ">", "≥", "≤"],
  time: [
    "é",
    "não é",
    "esta vazio",
    "não esta vazio",
    "é exatamente ou antes de",
    "é exatamente ou depois de",
    "antes de",
    "depois de",
  ],
  date: [
    "é",
    "não é",
    "esta vazio",
    "não esta vazio",
    "é exatamente ou antes de",
    "é exatamente ou depois de",
    "antes de",
    "depois de",
  ],
  address: [
    "é",
    "não é",
    "contem",
    "não contem",
    "começa com",
    "não começa com",
    "termina com",
    "não termina com",
    "esta vazio",
    "não esta vazio",
  ],
};

export interface FilterCondition {
  id: string;
  entity: EntityType;
  fieldId: string;
  operator: FilterOperator;
  value?: any;
}

export interface AdvancedFilter {
  id: string;
  name: string;
  visibility: "private" | "shared";
  saveSelectedColumns?: boolean;
  andConditions: FilterCondition[];
  orConditions: FilterCondition[];
  createdAt: string;
  updatedAt: string;
}

// Campos padrão pré-definidos conforme a solicitação do usuário
export const BASE_ENTITY_FIELDS: Record<EntityType, FieldDefinition[]> = {
  deal: [
    { id: "name", label: "Nome", type: "text", entity: "deal" },
    {
      id: "funil_id",
      label: "Funil de Vendas",
      type: "single_choice",
      entity: "deal",
    },
    { id: "amount", label: "Valor", type: "currency", entity: "deal" },
    {
      id: "status",
      label: "Status",
      type: "single_choice",
      entity: "deal",
      choices: [
        { value: "aberto", label: "Em Aberto" },
        { value: "ganho", label: "Ganho" },
        { value: "perdido", label: "Perdido" },
      ],
    },
    {
      id: "stage",
      label: "Etapa do Funil",
      type: "single_choice",
      entity: "deal",
    },
    {
      id: "temperatura",
      label: "Temperatura",
      type: "single_choice",
      entity: "deal",
      choices: [
        { value: "frio", label: "Frio" },
        { value: "morno", label: "Morno" },
        { value: "quente", label: "Quente" },
      ],
    },
    {
      id: "origem",
      label: "Origem",
      type: "single_choice",
      entity: "deal",
      choices: [
        { value: "indicacao", label: "Indicação" },
        { value: "inbound", label: "Inbound" },
        { value: "outbound", label: "Outbound" },
        { value: "google", label: "Google" },
        { value: "redes_sociais", label: "Redes Sociais" },
        { value: "outro", label: "Outro" },
      ],
    },
    {
      id: "canal_origem",
      label: "Canal de origem",
      type: "single_choice",
      entity: "deal",
      choices: [
        { value: "indicacao", label: "Indicação" },
        { value: "inbound", label: "Inbound" },
        { value: "outbound", label: "Outbound" },
        { value: "google", label: "Google" },
        { value: "redes_sociais", label: "Redes Sociais" },
        { value: "outro", label: "Outro" },
      ],
    },
    {
      id: "category",
      label: "Categoria",
      type: "single_choice",
      entity: "deal",
      choices: [
        { value: "default", label: "Padrão" },
        { value: "servicos", label: "Serviços" },
        { value: "consultoria", label: "Consultoria" },
        { value: "software", label: "Software" },
        { value: "outros", label: "Outros" },
      ],
    },
    { id: "sales_id", label: "Proprietário", type: "user", entity: "deal" },
    {
      id: "company_id",
      label: "Organização",
      type: "autocomplete",
      entity: "deal",
    },
    {
      id: "contact_id",
      label: "Pessoa de contato",
      type: "autocomplete",
      entity: "deal",
    },
    {
      id: "participants",
      label: "Participantes",
      type: "multiple_choice",
      entity: "deal",
    },
    {
      id: "expected_closing_date",
      label: "Data de fechamento esperada",
      type: "date",
      entity: "deal",
    },
    {
      id: "created_at",
      label: "Negócio criado em",
      type: "date",
      entity: "deal",
    },
    { id: "updated_at", label: "Atualizado em", type: "date", entity: "deal" },
    { id: "won_at", label: "Ganho em", type: "date", entity: "deal" },
    { id: "lost_at", label: "Data de perda", type: "date", entity: "deal" },
    {
      id: "motivo_perda",
      label: "Motivo da perda",
      type: "text",
      entity: "deal",
    },
    {
      id: "stage_entered_at",
      label: "Data de entrada na etapa",
      type: "date",
      entity: "deal",
    },
    {
      id: "stage_changed_at",
      label: "Última alteração de etapa",
      type: "date",
      entity: "deal",
    },
    {
      id: "last_activity_date",
      label: "Data da última atividade",
      type: "date",
      entity: "deal",
    },
    {
      id: "next_activity_date",
      label: "Próxima atividade em",
      type: "date",
      entity: "deal",
    },
    {
      id: "total_activities",
      label: "Total de atividades",
      type: "number",
      entity: "deal",
    },
    {
      id: "done_activities",
      label: "Atividades concluídas",
      type: "number",
      entity: "deal",
    },
    {
      id: "pending_activities",
      label: "Atividades por fazer",
      type: "number",
      entity: "deal",
    },
    { id: "created_by", label: "Criado por", type: "user", entity: "deal" },
    {
      id: "closed_at",
      label: "Negócio fechado em",
      type: "date",
      entity: "deal",
    },
    { id: "tags", label: "Etiqueta", type: "multiple_choice", entity: "deal" },
  ],
  company: [
    { id: "name", label: "Nome", type: "text", entity: "company" },
    {
      id: "sector",
      label: "Setor",
      type: "single_choice",
      entity: "company",
    },
    { id: "address", label: "Endereço", type: "address", entity: "company" },
    {
      id: "size",
      label: "Número de funcionários",
      type: "number",
      entity: "company",
    },
    {
      id: "linkedin_url",
      label: "Perfil do LinkedIn",
      type: "text",
      entity: "company",
    },
    { id: "sales_id", label: "Proprietário", type: "user", entity: "company" },
    { id: "nb_contacts", label: "Pessoas", type: "number", entity: "company" },
    {
      id: "open_deals",
      label: "Negócios em aberto",
      type: "number",
      entity: "company",
    },
    {
      id: "closed_deals",
      label: "Negócios fechados",
      type: "number",
      entity: "company",
    },
    {
      id: "won_deals",
      label: "Negócios ganhos",
      type: "number",
      entity: "company",
    },
    {
      id: "lost_deals",
      label: "Negócios perdidos",
      type: "number",
      entity: "company",
    },
    {
      id: "done_activities",
      label: "Atividades concluídas",
      type: "number",
      entity: "company",
    },
    {
      id: "pending_activities",
      label: "Atividades para fazer",
      type: "number",
      entity: "company",
    },
    {
      id: "email_messages_count",
      label: "Número de mensagens de e-mail",
      type: "number",
      entity: "company",
    },
    {
      id: "last_activity_date",
      label: "Data da última atividade",
      type: "date",
      entity: "company",
    },
    {
      id: "next_activity_date",
      label: "Próxima atividade em",
      type: "date",
      entity: "company",
    },
    {
      id: "created_at",
      label: "Organização criada",
      type: "date",
      entity: "company",
    },
    {
      id: "updated_at",
      label: "Atualizado em",
      type: "date",
      entity: "company",
    },
    {
      id: "tags",
      label: "Etiquetas",
      type: "multiple_choice",
      entity: "company",
    },
  ],
  contact: [
    { id: "first_name", label: "Nome", type: "text", entity: "contact" },
    { id: "last_name", label: "Sobrenome", type: "text", entity: "contact" },
    { id: "email", label: "E-mail", type: "text", entity: "contact" },
    { id: "phone", label: "Telefone", type: "text", entity: "contact" },
    {
      id: "gender",
      label: "Gênero",
      type: "single_choice",
      entity: "contact",
      choices: [
        { value: "male", label: "Masculino" },
        { value: "female", label: "Feminino" },
        { value: "other", label: "Outro" },
      ],
    },
    {
      id: "company_id",
      label: "Organização",
      type: "autocomplete",
      entity: "contact",
    },
    { id: "title", label: "Cargo", type: "text", entity: "contact" },
    { id: "sales_id", label: "Proprietário", type: "user", entity: "contact" },
    { id: "nb_tasks", label: "Atividades", type: "number", entity: "contact" },
    {
      id: "tags",
      label: "Etiquetas",
      type: "multiple_choice",
      entity: "contact",
    },
    { id: "first_seen", label: "Criado em", type: "date", entity: "contact" },
    {
      id: "last_seen",
      label: "Atualizado em",
      type: "date",
      entity: "contact",
    },
  ],
  activity: [
    { id: "title", label: "Assunto", type: "text", entity: "activity" },
    {
      id: "type",
      label: "Tipo",
      type: "single_choice",
      entity: "activity",
      choices: [
        { value: "call", label: "Ligação" },
        { value: "email", label: "E-mail" },
        { value: "meeting", label: "Reunião" },
        { value: "reminder", label: "Lembrete" },
        { value: "lunch", label: "Almoço" },
        { value: "other", label: "Outro" },
      ],
    },
    {
      id: "priority",
      label: "Prioridade",
      type: "single_choice",
      entity: "activity",
      choices: [
        { value: "low", label: "Baixa" },
        { value: "medium", label: "Média" },
        { value: "high", label: "Alta" },
      ],
    },
    {
      id: "done",
      label: "Concluído",
      type: "single_choice",
      entity: "activity",
      choices: [
        { value: "true", label: "Sim" },
        { value: "false", label: "Não" },
      ],
    },
    {
      id: "due_date",
      label: "Data de vencimento",
      type: "date",
      entity: "activity",
    },
    {
      id: "due_time",
      label: "Horário de vencimento",
      type: "time",
      entity: "activity",
    },
    {
      id: "completed_at",
      label: "Data e hora de conclusão",
      type: "date",
      entity: "activity",
    },
    {
      id: "description",
      label: "Descrição pública",
      type: "long_text",
      entity: "activity",
    },
    { id: "meet_url", label: "meet URL", type: "text", entity: "activity" },
    {
      id: "assigned_to",
      label: "Atribuído a usuário",
      type: "user",
      entity: "activity",
    },
    { id: "created_by", label: "Criado por", type: "user", entity: "activity" },
    {
      id: "contact_id",
      label: "Cliente da atividade",
      type: "autocomplete",
      entity: "activity",
    },
    {
      id: "company_id",
      label: "Organização",
      type: "autocomplete",
      entity: "activity",
    },
    {
      id: "deal_id",
      label: "Negócio",
      type: "autocomplete",
      entity: "activity",
    },
    {
      id: "created_at",
      label: "Data criação",
      type: "date",
      entity: "activity",
    },
    {
      id: "updated_at",
      label: "Atualizado em",
      type: "date",
      entity: "activity",
    },
    {
      id: "last_notification_time",
      label: "Hora da última notificação",
      type: "time",
      entity: "activity",
    },
  ],
  lead: [
    { id: "name", label: "Nome do Lead", type: "text", entity: "lead" },
    { id: "status", label: "Status", type: "single_choice", entity: "lead" },
    { id: "origem", label: "Origem", type: "text", entity: "lead" },
    {
      id: "temperatura",
      label: "Temperatura",
      type: "single_choice",
      entity: "lead",
    },
    { id: "created_at", label: "Criado em", type: "date", entity: "lead" },
  ],
  product: [
    { id: "name", label: "Nome do Produto", type: "text", entity: "product" },
    { id: "code", label: "Código", type: "text", entity: "product" },
    { id: "price", label: "Preço", type: "currency", entity: "product" },
    {
      id: "category",
      label: "Categoria",
      type: "single_choice",
      entity: "product",
    },
    { id: "active", label: "Ativo", type: "single_choice", entity: "product" },
  ],
};
